import moment from 'moment';
import { createSelector } from 'reselect';
import { AppState } from '../';
import { ICreativity, TypeOfSorts } from './types';
import { Filter, filtersSelector } from '../filters';
import { IVisibility, Status } from '../creativities';

export const selectCreativities = (emergency = false) => (state: AppState) => {
  const filteredSortedCreativities = state.creativities.all.filter(
    creativity =>
      emergency
        ? creativity.emergency
        : !creativity.emergency && creativity.status !== Status.CREATED
  );
  return filteredSortedCreativities;
};

export const selectCreativitiesById = (
  state: AppState,
  creativityId: string
): ICreativity | null =>
  (creativityId &&
    state.creativities.all.find(
      creativity => creativity._id === creativityId
    )) ||
  null;

const parseSortableDate = (value?: string) => {
  if (!value) {
    return null;
  }

  const formats = [moment.ISO_8601, 'DD/MM/YYYY', 'D/M/YYYY', 'DD-MM-YYYY'];
  const parsedDate = moment(value, formats, true);

  return parsedDate.isValid() ? parsedDate.valueOf() : null;
};

const getSortValue = (creative: ICreativity, sort: TypeOfSorts) => {
  switch (sort) {
    case TypeOfSorts.NAME:
      return (creative.name || '').toLocaleLowerCase();
    case TypeOfSorts.CREATION:
      return parseSortableDate(creative.createdAt);
    case TypeOfSorts.START:
      return parseSortableDate(creative.startDate);
    case TypeOfSorts.END:
      return parseSortableDate(creative.endDate);
    default:
      return null;
  }
};

const compareCreativities = (
  left: ICreativity,
  right: ICreativity,
  sort: TypeOfSorts
) => {
  const leftValue = getSortValue(left, sort);
  const rightValue = getSortValue(right, sort);

  if (leftValue === null && rightValue === null) {
    return 0;
  }

  if (leftValue === null) {
    return 1;
  }

  if (rightValue === null) {
    return -1;
  }

  if (typeof leftValue === 'string' && typeof rightValue === 'string') {
    return leftValue.localeCompare(rightValue);
  }

  return Number(leftValue) - Number(rightValue);
};

const sortCreativities = (items: ICreativity[], sort?: string) => {
  if (!sort || sort === TypeOfSorts.NONE) {
    return items;
  }

  return [...items].sort((left, right) =>
    compareCreativities(left, right, sort as TypeOfSorts)
  );
};

const sortSelector = (state: AppState) => state.creativities.sort;

export const selectFilteredCreativities = (type: Filter) =>
  createSelector(
    selectCreativities(),
    filtersSelector(type),
    sortSelector,
    (items, filter, sort) => {
      const filteredItems = filter ? items.filter(filter) : items;

      return sortCreativities(filteredItems, sort);
    }
  );

export const selectCurrentCreativities = createSelector(
  selectFilteredCreativities(Filter.GROUP),
  creativities => {
    if (!creativities) {
      return [];
    }
    return creativities.filter(
      creative =>
        isPublished(creative) && hasStarted(creative) && !hasFinished(creative)
    );
  }
);

export const selectNextCreativities = createSelector(
  selectFilteredCreativities(Filter.GROUP),
  creativities => {
    if (!creativities) {
      return [];
    }
    return creativities.filter(
      creative =>
        isPublished(creative) && !hasStarted(creative) && !hasFinished(creative)
    );
  }
);

const isPublished = ({ publishedAt, status }: ICreativity) => {
  return publishedAt || status === Status.PUBLISHED;
};

const hasStarted = ({ startDate }: ICreativity) => {
  const date = moment(startDate, 'D/M/YYYY');
  const today = moment().startOf('day');
  return date.isSameOrBefore(today);
};

const hasFinished = ({ endDate }: ICreativity) => {
  const date = moment(endDate, 'D/M/YYYY');
  const today = moment().startOf('day');
  return today.isAfter(date);
};

export const getPublishedEmergency = (state: AppState) =>
  state.creativities.all.find(
    emergency => emergency.status === Status.PUBLISHED && !!emergency.emergency
  );

export const selectEmergenciesById = (state: AppState, emergencyId: string) =>
  emergencyId &&
  state.creativities.all.find(emergency => emergency._id === emergencyId);

export const hasVisibilitySelector = (
  state: AppState,
  creativityId: string
) => {
  const creativity = selectCreativitiesById(state, creativityId);

  if (!creativity) {
    return false;
  }

  const { visibility } = creativity;

  if (!visibility) {
    return false;
  }

  return isVisible(visibility) || isConditional(visibility);
};

const isVisible = (visibility: IVisibility) => visibility.state === 'visible';
const isConditional = (visibility: IVisibility) =>
  visibility.state === 'conditional' &&
  visibility.rules &&
  visibility.rules.find(
    rule => rule.type === 'date' && rule.comparator == 'gte'
  ) &&
  visibility.rules.find(
    rule => rule.type === 'date' && rule.comparator == 'lte'
  );

export const hasLocationSelector = (state: AppState, creativityId: string) => {
  const creativity = selectCreativitiesById(state, creativityId);

  if (!creativity) {
    return false;
  }

  return (
    creativity.allPlayers ||
    (creativity.players && creativity.players.length) ||
    (creativity.groups && creativity.groups.length)
  );
};

export const hasAllPlayersSelected = (
  state: AppState,
  creativityId: string
) => {
  const creativity = selectCreativitiesById(state, creativityId);

  return creativity && !!creativity.allPlayers;
};

export const selectNumberOfSelected = (
  state: AppState,
  creativityId: string
) => {
  const creativity = selectCreativitiesById(state, creativityId);

  const players =
    (creativity && creativity.players && creativity.players.length) || 0;

  const groups =
    (creativity && creativity.groups && creativity.groups.length) || 0;

  return { players, groups };
};

export const hasVideosWithoutSourcesSelector = (
  state: AppState,
  creativityId: string
) => {
  const creativity = selectCreativitiesById(state, creativityId);

  if (creativity) {
    const { slides } = creativity;

    for (let s = 0; s < slides.length; s++) {
      const { elements } = slides[s];

      for (let e = 0; e < elements.length; e++) {
        const element = elements[e];

        if (element.type === 'video' && !element.sources) {
          return true;
        }
      }
    }
  }

  return false;
};

export const selectVisibilityWeekday = (
  state: AppState,
  creativityId: string
) => {
  const creativity = selectCreativitiesById(state, creativityId);

  if (!creativity) {
    return false;
  }

  const { visibility } = creativity;

  if (!visibility) {
    return false;
  }

  const weekDayRule =
    visibility &&
    visibility.rules &&
    visibility.rules.find(rule => rule.type === 'weekDay');

  return weekDayRule && weekDayRule.value;
};
